import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, Inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { catchError, of } from 'rxjs';

import { PYTHON_URL } from '@/shared/config/base-url.token';

interface ExtraRequestsPaymentResponse {
  paymentUrl: string;
  paymentId: string;
}

enum PaymentRequiredState {
  Idle = 'idle',
  Visible = 'visible',
}

const PAYMENT_REQUIRED_COOLDOWN_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class PaymentRequiredService {
  readonly visible$: Signal<boolean>;
  readonly paying$: WritableSignal<boolean>;
  readonly paymentError$: WritableSignal<boolean>;

  private readonly state$: WritableSignal<PaymentRequiredState>;
  private cooldownStartedAt: number | null;

  constructor(
    @Inject(PYTHON_URL) private readonly pythonUrl: string,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly http: HttpClient,
  ) {
    this.state$ = signal(PaymentRequiredState.Idle);
    this.visible$ = computed(() => this.state$() === PaymentRequiredState.Visible);
    this.paying$ = signal(false);
    this.paymentError$ = signal(false);
    this.cooldownStartedAt = null;
  }

  show(): void {
    const now = Date.now();
    const cooldownActive =
      this.cooldownStartedAt !== null && now - this.cooldownStartedAt < PAYMENT_REQUIRED_COOLDOWN_MS;

    if (this.state$() === PaymentRequiredState.Visible || cooldownActive) {
      return;
    }

    this.paymentError$.set(false);
    this.state$.set(PaymentRequiredState.Visible);
  }

  cancel(): void {
    this.cooldownStartedAt = Date.now();
    this.state$.set(PaymentRequiredState.Idle);
    this.paying$.set(false);
    this.paymentError$.set(false);
  }

  pay(): void {
    if (this.paying$()) {
      return;
    }

    const currentWindow = this.document.defaultView;
    if (!currentWindow) {
      return;
    }

    this.paying$.set(true);
    this.paymentError$.set(false);

    this.http
      .post<ExtraRequestsPaymentResponse>(
        `${this.pythonUrl}/auth/api/tariffs/pay-extra`,
        { returnTo: currentWindow.location.href },
        { withCredentials: true },
      )
      .pipe(catchError(() => of(null)))
      .subscribe(response => {
        if (!response?.paymentUrl) {
          this.paying$.set(false);
          this.paymentError$.set(true);
          this.cooldownStartedAt = Date.now();
          return;
        }
        this.paying$.set(false);
        this.state$.set(PaymentRequiredState.Idle);
        currentWindow.location.assign(response.paymentUrl);
      });
  }
}
